import {
  HeaderCellSort,
  useSort,
} from "@table-library/react-table-library/sort";
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table,
} from "@table-library/react-table-library/table";
import type { TableNode } from "@table-library/react-table-library/types";
import { useEffect, useState } from "react";
import { Gear, GearFill, PersonPlus } from "react-bootstrap-icons";
import {
  type ContactInfo,
  ContactInfoCategory,
  ContactInfoType,
  ReportingTimeBucket,
  ReportType,
  ToggleableFeature,
  type ToggleFeatureRequest,
  type UserInvite,
  type UserType,
} from "@/briefpoint-client";
import { futureDeactivationDate, toLocaleUSDateString } from "@/lib/utils";
import ActivateModal, { type ActivateModel } from "./ActivateModal";
import InviteModal from "./InviteModal";
import RunReportButtonGroup from "./RunReportButtonGroup";
import "./FirmUserList.scss";
import { getTheme } from "@table-library/react-table-library/baseline";
import { useTheme } from "@table-library/react-table-library/theme";
import { Spinner } from "react-bootstrap";
import Button from "@/components/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFirmApi, useReportsApi } from "@/hooks/useApi";
import type { UserResponse } from "@/lib/types";

function FeatureToggle({
  user,
  firmId,
  onFeatureToggle,
}: {
  user: UserResponse;
  firmId: string;
  onFeatureToggle: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<
    Map<ToggleableFeature, boolean>
  >(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const firmApi = useFirmApi();

  // Initialize pending changes when user changes
  useEffect(() => {
    const initialChanges = new Map<ToggleableFeature, boolean>();
    Object.values(ToggleableFeature).forEach((feature) => {
      const isEnabled = !user.disabledFeatures.has(feature);
      initialChanges.set(feature, isEnabled);
    });
    setPendingChanges(initialChanges);
    setHasChanges(false);
  }, [user]);

  // Check if there are any changes
  useEffect(() => {
    const hasAnyChanges = Object.values(ToggleableFeature).some((feature) => {
      const currentEnabled = !user.disabledFeatures.has(feature);
      const pendingEnabled = pendingChanges.get(feature) ?? currentEnabled;
      return currentEnabled !== pendingEnabled;
    });
    setHasChanges(hasAnyChanges);
  }, [pendingChanges, user.disabledFeatures]);

  const handleFeatureToggle = (
    feature: ToggleableFeature,
    enabled: boolean,
  ) => {
    setPendingChanges((prev) => new Map(prev.set(feature, enabled)));
  };

  const handleCancel = () => {
    // Reset to original state
    const originalChanges = new Map<ToggleableFeature, boolean>();
    Object.values(ToggleableFeature).forEach((feature) => {
      const isEnabled = !user.disabledFeatures.has(feature);
      originalChanges.set(feature, isEnabled);
    });
    setPendingChanges(originalChanges);
    setIsOpen(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Find all features that have changed
      const changes: ToggleFeatureRequest[] = [];
      Object.values(ToggleableFeature).forEach((feature) => {
        const currentEnabled = !user.disabledFeatures.has(feature);
        const pendingEnabled = pendingChanges.get(feature) ?? currentEnabled;

        if (currentEnabled !== pendingEnabled) {
          changes.push({
            feature,
            isDisabled: !pendingEnabled,
          });
        }
      });

      // Make API calls for each changed feature
      for (const change of changes) {
        await firmApi.firmToggleUserFeature({
          firmId,
          userId: user.id,
          toggleFeatureRequest: change,
        });
      }

      // Close the menu and trigger refresh
      setIsOpen(false);
      onFeatureToggle();
    } catch (error) {
      console.error("Failed to toggle user features:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsSaving(false);
    }
  };

  const disabledFeaturesCount = user.disabledFeatures.size;
  const GearIcon = disabledFeaturesCount === 0 ? GearFill : Gear;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="link"
          className="p-1"
          title="Enable/Disable Features"
        >
          <GearIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5 font-medium text-sm">
          Enable/Disable Features
        </div>
        <DropdownMenuSeparator />

        {Object.values(ToggleableFeature).map((feature) => {
          const isEnabled =
            pendingChanges.get(feature) ?? !user.disabledFeatures.has(feature);
          return (
            <div
              key={feature}
              className="d-flex px-2 py-1.5 align-items-center"
            >
              <input
                type="checkbox"
                id={`feature-${feature}-${user.id}`}
                className="form-check-input me-2"
                checked={isEnabled}
                onChange={(e) => handleFeatureToggle(feature, e.target.checked)}
              />
              <label
                htmlFor={`feature-${feature}-${user.id}`}
                className="form-check-label flex-grow-1"
              >
                {feature}
              </label>
            </div>
          );
        })}

        <DropdownMenuSeparator />
        <div className="d-flex justify-content-end gap-2 p-2 align-items-center">
          <Button
            type="button"
            variant="link"
            className="p-0 text-muted"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={!hasChanges || isSaving}
            onClick={handleSave}
          >
            {isSaving ? (
              <>
                <Spinner size="sm" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Toggle({
  isCanonicallyInactive,
  canonicalDeactivationDate,
  onActivateDeactivate,
  className,
}: {
  isCanonicallyInactive: boolean;
  canonicalDeactivationDate: string;
  onActivateDeactivate: () => void;
  className?: string;
}) {
  // A) we expect pill toggles to update instantly so we update local state instantly,
  // initialized by canonical value
  const [isLocallyInactive, setIsLocallyInactive] = useState(
    isCanonicallyInactive,
  );

  useEffect(() => {
    // B) however, we don't want a inconsistent UI should the call fail, so let's
    // register a timeout to eventually correct the local state
    const timeout = setTimeout(() => {
      if (isLocallyInactive !== isCanonicallyInactive) {
        setIsLocallyInactive(isCanonicallyInactive);
      }
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [isLocallyInactive, isCanonicallyInactive]);

  useEffect(() => {
    // C) inversely, if canonical is somehow updated, we need a way of correcting local state
    setIsLocallyInactive(isCanonicallyInactive);
  }, [isCanonicallyInactive]);

  const onToggle = () => {
    // D) if we're attempting a toggle and we don't have the correct local state, bail
    if (isLocallyInactive !== isCanonicallyInactive) {
      return;
    }
    setIsLocallyInactive((x) => !x);
    onActivateDeactivate();
  };
  let message = "";
  if (isCanonicallyInactive && isLocallyInactive && canonicalDeactivationDate) {
    message = `Active ${canonicalDeactivationDate}`;
  } else if (isCanonicallyInactive !== isLocallyInactive) {
    message = "Saving...";
  }

  return (
    <div className={`active-container ${className}`}>
      <div className="toggle">
        <div className={isLocallyInactive ? "inactive-status" : undefined}>
          Inactive
        </div>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button*/}
        <div
          className={`pill ${isLocallyInactive ? "" : "active-pill"} ${
            isLocallyInactive !== isCanonicallyInactive
              ? "toggle-disabled"
              : undefined
          }`}
          onClick={onToggle}
        >
          <div className={`pip ${isLocallyInactive ? "" : "active-pip"}`} />
        </div>
        <div className={isLocallyInactive ? undefined : "active-status"}>
          Active
        </div>
      </div>
      <div className="active-container-deactivating">{message}</div>
    </div>
  );
}

export default function FirmUserList({
  users,
  invites,
  firmId,
  onInvite,
  onCancelInvite,
  onActivateDeactivate,
  onFeatureToggle,
}: {
  users: UserResponse[];
  invites: UserInvite[] | null;
  firmId: string;
  onInvite: (email: string, type: UserType) => Promise<void>;
  onCancelInvite: (inviteId: string) => Promise<void>;
  onActivateDeactivate: (userId: string, activate: boolean) => Promise<void>;
  onFeatureToggle?: () => void;
}) {
  const userList = {
    nodes: users.map((user) => ({ ...user, id: user.id as string })),
  };
  const inviteList = invites && {
    nodes: invites.map((invite) => ({ ...invite, id: invite.id as string })),
  };
  const [showModal, setShowModal] = useState(false);
  const [activateModel, setActivateModel] = useState<
    ActivateModel | undefined
  >();
  const reportsApi = useReportsApi();

  const userSort = useSort(
    userList,
    {
      state: {
        sortKey: "CREATED",
        reverse: true,
      },
    },
    {
      sortFns: {
        NAME: (array) =>
          array.sort((a, b) => {
            const aLastName = a.lastName || "";
            const bLastName = b.lastName || "";
            return aLastName.localeCompare(bLastName);
          }),
        CREATED: (array) =>
          array.sort(
            (a, b) =>
              Date.parse(a.createdOn || "") - Date.parse(b.createdOn || ""),
          ),
      },
    },
  );
  const inviteSort = useSort(
    userList,
    {
      state: {
        sortKey: "CREATED",
        reverse: true,
      },
    },
    {
      sortFns: {
        EMAIL: (array) =>
          array.sort((a, b) => {
            const aId = a.id?.toString() || "";
            const bId = b.id?.toString() || "";
            return aId.localeCompare(bId);
          }),
        CREATED: (array) =>
          array.sort(
            (a, b) =>
              Date.parse((a.lastResent ?? a.firstSent) || "") -
              Date.parse((b.lastResent ?? b.firstSent) || ""),
          ),
      },
    },
  );

  function canResend(item: TableNode) {
    const dateNum = Date.parse(
      (item.lastResent ?? item.firstSent)?.toString() || "",
    );

    if (dateNum) {
      const date = new Date(dateNum);
      date.setMinutes(date.getMinutes() + 5);
      return new Date().toISOString() > date.toISOString();
    }

    return true;
  }

  async function generateUsageReport(
    type: ReportType,
    bucket: ReportingTimeBucket,
  ) {
    try {
      const result = await reportsApi.reportsRunReport({
        runReportRequest: {
          reportType: type,
          reportingTimeBucket: bucket,
        },
      });
      const url = window.URL.createObjectURL(
        new Blob([result.csvContent || ""], { type: "text/csv" }),
      );
      const link = window.document.createElement("a");
      link.href = url;
      link.download =
        result.suggestedFileName ||
        `Briefpoint Usage Report ${toLocaleUSDateString(new Date())}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating usage report:", error);
    }
  }

  const pendingInvitesTheme = useTheme([
    getTheme(),
    {
      Table: `
        grid-template-columns: 50% 1fr auto;
      `,
      BaseCell: `
        &:nth-of-type(1) { border-right: 1px solid #dee2e6; } /* Email */
      `,
    },
  ]);

  const usersTheme = useTheme([
    getTheme(),
    {
      Table: `
        grid-template-columns: repeat(4, 1fr) 40%;
      `,
      BaseCell: `
        &:nth-of-type(1), &:nth-of-type(2) { border-right: 1px solid #dee2e6; } /* Email */
      `,
    },
  ]);

  return (
    <>
      <div
        className="d-flex justify-content-end align-items-center"
        style={{ gap: "8px" }}
      >
        <RunReportButtonGroup onGenerateReport={generateUsageReport} />

        <Button
          type="button"
          variant="default"
          size="lg"
          onClick={() => setShowModal(true)}
        >
          <PersonPlus /> Invite User
        </Button>
      </div>
      {invites && !!invites.length && inviteList && (
        <>
          <h2 className="mb-3">Pending Invites</h2>
          <Table
            data={inviteList}
            sort={inviteSort}
            theme={pendingInvitesTheme}
            className="mb-6"
          >
            {(tableList: TableNode[]) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCellSort sortKey="EMAIL">
                      Email Address
                    </HeaderCellSort>
                    <HeaderCellSort sortKey="CREATED">
                      Date Invite Sent
                    </HeaderCellSort>
                    <HeaderCell />
                  </HeaderRow>
                </Header>
                <Body>
                  {tableList.map((item) => (
                    <Row key={item.id} item={item}>
                      <Cell>{item.emailAddress} </Cell>
                      <Cell>
                        <span>
                          {toLocaleUSDateString(
                            item.lastResent ?? item.firstSent,
                          )}
                        </span>
                      </Cell>
                      <Cell>
                        <div className="d-flex align-items-center">
                          {canResend(item) ? (
                            <Button
                              type="button"
                              variant="link"
                              className="text-muted"
                              onClick={() =>
                                onInvite(item.emailAddress, item.type)
                              }
                            >
                              Resend Invite
                            </Button>
                          ) : (
                            <span>
                              Please wait a few minutes before resending
                            </span>
                          )}

                          {canResend(item) && <span>|</span>}

                          <Button
                            type="button"
                            variant="link"
                            className="text-danger"
                            onClick={() => onCancelInvite(item.id.toString())}
                          >
                            Cancel Invite
                          </Button>
                        </div>
                      </Cell>
                    </Row>
                  ))}
                </Body>
              </>
            )}
          </Table>
        </>
      )}

      <h2 className="mt-4 mb-3">Users</h2>
      <ActivateModal
        model={activateModel}
        onClose={() => {
          setActivateModel(undefined);
        }}
        onSubmit={onActivateDeactivate}
      />
      <Table
        id="firm-user-table"
        data={userList}
        sort={userSort}
        theme={usersTheme}
      >
        {(tableList: TableNode[]) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCellSort sortKey="NAME">Name</HeaderCellSort>
                <HeaderCell>Email Address</HeaderCell>
                <HeaderCellSort sortKey="CREATED">Date Joined</HeaderCellSort>
                <HeaderCell>User Type</HeaderCell>
                <HeaderCell />
              </HeaderRow>
            </Header>

            <Body>
              {tableList.map((item) => (
                <Row key={item.id} item={item}>
                  <Cell>
                    <div className="d-flex gap-2 align-items-center">
                      <span>{`${item.lastName}, ${item.firstName}`}</span>
                      <FeatureToggle
                        user={item as UserResponse}
                        firmId={firmId}
                        onFeatureToggle={onFeatureToggle || (() => {})}
                      />
                    </div>
                  </Cell>
                  <Cell>
                    {item.contactInfo?.find(
                      (x: ContactInfo) =>
                        x.type === ContactInfoType.Email &&
                        x.category === ContactInfoCategory.Office,
                    )?.value || "Unknown Email"}
                  </Cell>
                  <Cell>{toLocaleUSDateString(item.createdOn)}</Cell>
                  <Cell>{item.type}</Cell>
                  <Cell>
                    <Toggle
                      className="my-2"
                      isCanonicallyInactive={
                        item.isInactive ||
                        futureDeactivationDate(item.deactivationDate)
                      }
                      canonicalDeactivationDate={futureDeactivationDate(
                        item.deactivationDate,
                      )}
                      onActivateDeactivate={() =>
                        onActivateDeactivate(
                          item.id.toString(),
                          item.isInactive ||
                            futureDeactivationDate(item.deactivationDate),
                        )
                      }
                    />
                  </Cell>
                </Row>
              ))}
            </Body>
          </>
        )}
      </Table>

      <InviteModal
        isShow={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        onSubmit={onInvite}
      />
    </>
  );
}
